import { Avatar, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { IUserMin } from 'features/settings/users/types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  // 1. STATI APERTI
  // OPEN: Arancione chiaro/Beige
  OPEN: { color: 'rgb(180, 83, 9)', bg: 'rgb(254, 243, 219)' },
  // INTERVENTION_REQUESTED: Arancione ambra più intenso (Segnala azione richiesta)
  INTERVENTION_REQUESTED: { color: 'rgb(194, 65, 12)', bg: 'rgb(255, 237, 213)' },

  // 2. STATI CHIUSI / RISOLTI (Verde pastello)
  PHONE_RESOLVED: { color: 'rgb(21, 128, 61)', bg: 'rgb(220, 252, 231)' },
  TECHNICIAN_RESOLVED: { color: 'rgb(21, 128, 61)', bg: 'rgb(220, 252, 231)' },
  INTERVENTION_COMPLETED: { color: 'rgb(21, 128, 61)', bg: 'rgb(220, 252, 231)' },
  INTERVENTION_COMPLETED_MISSING_REPORT: { color: 'rgb(21, 128, 61)', bg: 'rgb(220, 252, 231)' },

  // 3. STATI TECNICI / IN CORSO (Blu pastello)
  TECHNICIAN_CREATED: { color: 'rgb(29, 78, 216)', bg: 'rgb(219, 234, 254)' },
  INTERVENTION_PENDING: { color: 'rgb(29, 78, 216)', bg: 'rgb(219, 234, 254)' },

  // 4. STATO NON DETERMINATO (Grigio pastello)
  UNDETERMINED: { color: 'rgb(71, 85, 105)', bg: 'rgb(241, 245, 249)' },

  // Fallback di sicurezza
  DEFAULT: { color: 'rgb(71, 85, 105)', bg: 'rgb(241, 245, 249)' }
};

export const TickectColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  const formatDateTime = (iso?: string | Date | null) => {
    if (!iso) return { date: '—', time: '' };
    const d = dayjs(iso);
    return { date: d.format('DD/MM/YYYY'), time: d.format('HH:mm') };
  };

  const getStatusStyle = (display_name?: string) => {
    return STATUS_STYLES[display_name ?? ''] ?? STATUS_STYLES.DEFAULT;
  };

  return [
    {
      field: 'created_at',
      headerName: 'Data',
      type: 'date',
      flex: 1,
      minWidth: 60,
      maxWidth: 120,
      valueGetter: (params) => {
        const rawValue = params?.value;
        if (!rawValue) return null;

        if (rawValue instanceof Date) return isNaN(rawValue.getTime()) ? null : rawValue;

        if (typeof rawValue === 'string' && rawValue.includes('/')) {
          try {
            const [datePart, timePart] = rawValue.trim().split(' ');
            const [day, month, year] = datePart.split('/');

            const formattedIsoString = `${year}-${month}-${day}T${timePart || '00:00'}:00`;
            const parsedDate = new Date(formattedIsoString);

            return isNaN(parsedDate.getTime()) ? null : parsedDate;
          } catch (error) {
            console.error('Error parsing custom European date token format:', error);
            return null;
          }
        }

        const fallbackDate = new Date(rawValue);
        return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
      },
      renderCell: (params) => {
        if (!params.value) {
          return <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>;
        }

        const targetDate = params.value instanceof Date ? params.value : new Date(params.value);
        if (isNaN(targetDate.getTime())) {
          return (
            <span style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 500 }}>
              Data non valida
            </span>
          );
        }

        const { date, time } = formatDateTime(targetDate);

        return (
          <Stack justifyContent="center" alignItems="flex-start" sx={{ height: '100%', py: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>
              {date || '—'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
              {time || '—'}
            </Typography>
          </Stack>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 60,
      renderCell: (params) => {
        const ticket = params.row;
        const statusDisplay = ticket.status?.display_name ?? ticket.status_display ?? 'OPEN';
        const statusLabel = t(`entities.tickets.status_options.${statusDisplay}`);

        const statusStyle = getStatusStyle(statusDisplay);

        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{ height: '100%', width: '100%', py: 0.5, minWidth: 0 }}>
            <Chip
              label={statusLabel}
              size="medium"
              sx={{
                fontWeight: 500,
                borderRadius: '50px !important',
                height: 24,
                fontSize: '0.75rem',
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          </Stack>
        );
      }
    },
    {
      field: 'creation_user',
      headerName: 'Creato da',
      flex: 1,
      minWidth: 210,
      renderCell: (params) => {
        const operator: IUserMin =
          params.row.creation_user_detail || 'Operatore Laboratorio Analisi';

        const initials = operator.first_name
          ? operator.first_name
              .split(' ')
              .map((n: string) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()
          : '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: '#e6f7ff',
                color: '#1890ff',
                border: '1px solid #bae7ff',
                textTransform: 'uppercase'
              }}>
              {initials}
            </Avatar>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#262626',
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                  textTransform: 'capitalize'
                }}>
                {operator.full_name?.toLowerCase()}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8c8c8c', fontSize: '0.75rem' }}>
                {Array.isArray(operator.roles)
                  ? operator.roles
                      .map((role) => t(`roles.${role}`, { defaultValue: role }))
                      .join(', ')
                  : operator.roles
                  ? t(`roles.${operator.roles}`, { defaultValue: operator.roles })
                  : ''}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'approvation_user',
      headerName: 'Approvato da',
      flex: 1,
      minWidth: 210,
      renderCell: (params) => {
        const operator: IUserMin | null = params.row.approvation_user_detail || null;

        if (!operator) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
              <Typography
                variant="body2"
                sx={{ color: '#8c8c8c', fontStyle: 'italic', fontSize: '0.85rem' }}>
                Non approvato
              </Typography>
            </Box>
          );
        }

        const initials = operator.first_name
          ? operator.first_name
              .split(' ')
              .map((n: string) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()
          : '';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: '#e6f7ff',
                color: '#1890ff',
                border: '1px solid #bae7ff',
                textTransform: 'uppercase'
              }}>
              {initials}
            </Avatar>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#262626',
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                  textTransform: 'capitalize'
                }}>
                {operator.full_name?.toLowerCase()}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8c8c8c', fontSize: '0.75rem' }}>
                {Array.isArray(operator.roles)
                  ? operator.roles
                      .map((role) => t(`roles.${role}`, { defaultValue: role }))
                      .join(', ')
                  : operator.roles
                  ? t(`roles.${operator.roles}`, { defaultValue: operator.roles })
                  : ''}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'custom_actions',
      headerName: '',
      width: 160,
      sortable: false,
      filterable: false,
      aggregable: false,
      groupable: false,
      renderCell: (params) => {
        return (
          <Button
            variant="text"
            size="small"
            component={Link}
            to={`/tenant/equipments/ticketing/${params?.row?.id}`}
            sx={{
              whiteSpace: 'nowrap',
              textTransform: 'none'
            }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: '#1d70b8' }}>
              Vai a Dettagli Ticket
            </Typography>
          </Button>
        );
      }
    }
  ];
};
